import { useState, useMemo, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";

import moment from "moment";
import { NewsCard } from "./NewsCard";
import { NewsModalContent } from "./NewsModalContent";

import { Container, Pagination, Breadcrumb, Modal } from "components";
import { ListPageTitle } from "common";

import { useNews } from "./hooks/useNews";
import { generateImageLink } from "utils/generateImageLinks";

import { ENUM_FilterDateRangeActionType } from "reducers/filterDateRange";

import "./News.scss";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { convertRawToHtml } from "utils";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { languages_enum } from "redux/actions/globalSlice";

export function News() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { page } = useParams();

  const language = useSelector((state: RootState) => state.global.language);
  const prefix = language === languages_enum.GEORGIAN ? "" : `/${language}`;

  const [currentPage, setCurrentPage] = useState<number>(1);

  const { news, PAGE_SIZE, maxPages, filterDates, setFiltersDateRange } =
    useNews({ currentPage });

  const [pressedNext, setPressedNext] = useState(false);
  const [pressedPrev, setPressedPrev] = useState(false);

  async function asyncShowNextPageModal() {
    try {
      const result = await news;
      if (result && result.length > 0) {
        navigate(
          `${prefix}/news/${currentPage}/${result[0]?.id}/${result[0]?.slug}`
        );
        setPressedNext(false);
      }
    } catch (error) {
      console.error("Error navigating to news:", error);
    }
  }

  async function asyncShowPrevPageModal() {
    try {
      const result = await news;
      if (result && result.length > 0) {
        navigate(
          `${prefix}/news/${currentPage}/${result[result.length - 1]?.id}/${
            result[result.length - 1]?.slug
          }`
        );
        setPressedPrev(false);
      }
    } catch (error) {
      console.error("Error navigating to news:", error);
    }
  }

  useEffect(() => {
    if (pressedNext) asyncShowNextPageModal();
    if (pressedPrev) asyncShowPrevPageModal();
  }, [pressedNext, currentPage, news, pressedPrev]);

  const [currentNewsId, setCurrentNewsId] = useState<string | number>();

  const currentNewsIndex = useMemo(() => {
    return news?.findIndex((n: any) => n?.id === currentNewsId);
  }, [news, currentNewsId]);

  useEffect(() => {
    if (Number(page) <= maxPages && !pressedNext && !pressedPrev) {
      setCurrentPage(Number(page));
    }
  }, [page, maxPages]);

  const navigateCurrentNews = useCallback(
    (path: string) => {
      navigate(`${prefix}/news/${currentPage}/${path}`);
    },
    [currentPage, prefix]
  );

  return (
    <Container className="news">
      <ListPageTitle>
        {formatMessage({ id: "news.title", defaultMessage: "_news.title_" })}
      </ListPageTitle>

      <Breadcrumb
        links={[
          {
            label: formatMessage({
              id: "main.breadcrumb",
              defaultMessage: "_main.breadcrumb_",
            }),
            value: `${prefix}/`,
          },
          {
            label: formatMessage({
              id: "news.breadcrumb",
              defaultMessage: "_news.breadcrumb_",
            }),
            value: `${prefix}/news/1`,
          },
        ]}
      />

      <div className="news-content">
        {news?.map((n: any) => (
          <NewsCard
            key={n?.id}
            title={n?.title}
            text={convertRawToHtml(n?.content)}
            image={generateImageLink({ imageId: n?.cover_image })}
            date={moment(n.created_at).format("DD.MM.YYYY")}
            link={`${prefix}/news/${currentPage}/${n?.id}/${n?.slug}`}
          />
        ))}
      </div>

      <Pagination
        className="news-pagination"
        maxPages={maxPages}
        pageSize={PAGE_SIZE}
        currentPage={currentPage}
        onChange={(page: number) => {
          navigate(`${prefix}/news/${page}`);
        }}
      />

      <Routes>
        <Route
          path=":id/:slug"
          element={
            <Modal
              show={true}
              onClose={() => navigate(`${prefix}/news/${currentPage}`)}
              next={() => {
                if (currentNewsId == news[news.length - 1]?.id) {
                  if (currentPage !== maxPages) {
                    setCurrentPage(currentPage + 1);
                    setPressedNext(true);
                  }
                } else if (currentNewsIndex !== -1) {
                  navigateCurrentNews(
                    `${news[currentNewsIndex + 1]?.id}/${
                      news[currentNewsIndex + 1]?.slug
                    }`
                  );
                }
              }}
              prev={() => {
                if (currentNewsId == news[0]?.id) {
                  if (currentPage !== 1) {
                    setCurrentPage(currentPage - 1);
                    setPressedPrev(true);
                  }
                } else if (currentNewsIndex !== -1) {
                  navigateCurrentNews(
                    `${news[currentNewsIndex - 1]?.id}/${
                      news[currentNewsIndex - 1]?.slug
                    }`
                  );
                }
              }}
            >
              <NewsModalContent setCurrentNewsId={setCurrentNewsId} />
            </Modal>
          }
        />
      </Routes>
    </Container>
  );
}
